import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api/client";
import type {
  Balance,
  Category,
  ParentInvite,
  RecurringAllowance,
  Transaction,
  User,
} from "../api/types";
import { useAuth, useErrorHandler } from "../auth/AuthContext";
import { AllowanceList } from "../components/AllowanceList";
import { AllowanceModal } from "../components/AllowanceModal";
import { AvatarPicker } from "../components/AvatarPicker";
import { BalanceView } from "../components/BalanceView";
import { TransactionList } from "../components/TransactionList";
import { TransactionModal } from "../components/TransactionModal";
import { ErrorMessage, Field, Modal } from "../components/common";
import { DEFAULT_AVATAR, FALLBACK_AVATAR } from "../lib/avatars";
import { usePoll } from "../lib/usePoll";

const PAGE_SIZE = 25;

/** How often the child detail panel re-reads the child's money and settings. */
const POLL_MS = 15000;

export function ParentDashboard() {
  const { family } = useAuth();
  const toMessage = useErrorHandler();

  const [children, setChildren] = useState<User[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [addingChild, setAddingChild] = useState(false);
  const [editingSlug, setEditingSlug] = useState(false);

  const loadChildren = useCallback(async () => {
    try {
      const list = await api.listChildren();
      setChildren(list);
      // Keep the current selection if it still exists; otherwise fall back to
      // the first child so the page is never blank after a delete.
      setSelectedId((current) =>
        current && list.some((c) => c.id === current)
          ? current
          : (list[0]?.id ?? null),
      );
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setLoading(false);
    }
  }, [toMessage]);

  useEffect(() => {
    void loadChildren();
  }, [loadChildren]);

  const selected = children.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="page">
      <ErrorMessage>{error}</ErrorMessage>

      <div className="row row--between">
        <div>
          <h1>{family?.name ?? "Your family"}</h1>
          <p className="muted">
            Kids sign in with the family code{" "}
            <span className="family-code">{family?.slug}</span>{" "}
            <button
              type="button"
              className="link-button"
              onClick={() => setEditingSlug(true)}
            >
              Change
            </button>
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => setAddingChild(true)}
        >
          Add a kid
        </button>
      </div>

      <ParentsSection />

      {loading ? (
        <p className="muted">Loading…</p>
      ) : children.length === 0 ? (
        <div className="card empty">
          <p>No kids yet.</p>
          <p className="muted">
            Add your first one to start tracking their allowance.
          </p>
        </div>
      ) : (
        <>
          <div className="child-grid">
            {children.map((child) => (
              <button
                key={child.id}
                type="button"
                className={`child-card ${
                  child.id === selectedId ? "child-card--selected" : ""
                }`}
                onClick={() => setSelectedId(child.id)}
              >
                <span className="avatar">{child.avatar || FALLBACK_AVATAR}</span>
                <span>
                  <span style={{ fontWeight: 700, display: "block" }}>
                    {child.displayName}
                  </span>
                  <span className="faint">@{child.username}</span>
                </span>
              </button>
            ))}
          </div>

          {selected ? (
            <ChildDetail
              key={selected.id}
              child={selected}
              onChildChanged={loadChildren}
            />
          ) : null}
        </>
      )}

      {addingChild ? (
        <AddChildModal
          onClose={() => setAddingChild(false)}
          onCreated={async () => {
            setAddingChild(false);
            await loadChildren();
          }}
        />
      ) : null}

      {editingSlug ? (
        <EditFamilySlugModal onClose={() => setEditingSlug(false)} />
      ) : null}
    </div>
  );
}

/** Changes the family's login code. Every kid types this to sign in, so a
 * parent changing it should double-check before it takes effect. */
function EditFamilySlugModal({ onClose }: { onClose: () => void }) {
  const { family, updateIdentity } = useAuth();
  const toMessage = useErrorHandler();

  const [slug, setSlug] = useState(family?.slug ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const updated = await api.updateFamilySlug(slug);
      updateIdentity({ family: updated });
      onClose();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Change the family code" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorMessage>{error}</ErrorMessage>

        <Field
          label="Family code"
          hint="Lowercase letters, numbers, and hyphens, 3 to 32 characters. Every kid will need it next time they sign in."
        >
          <input
            className="input"
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
          />
        </Field>

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The family's parents: no hierarchy between them, so this is the same list
 * with a "Remove" button on every row rather than an owner plus a set of
 * guests. Inviting a co-parent and removing one both live here.
 */
function ParentsSection() {
  const { user, handleUnauthorized } = useAuth();
  const toMessage = useErrorHandler();

  const [parents, setParents] = useState<User[]>([]);
  const [error, setError] = useState("");
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<User | null>(null);

  const load = useCallback(async () => {
    try {
      const { members } = await api.family();
      setParents(members.filter((m) => m.role === "parent"));
    } catch (err) {
      setError(toMessage(err));
    }
  }, [toMessage]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doRemove() {
    if (!removing) return;
    try {
      await api.deleteParent(removing.id);
      const removedSelf = removing.id === user?.id;
      setRemoving(null);
      if (removedSelf) {
        // The server has already dropped this session along with the user
        // row — reflect that locally instead of waiting for the next request
        // to come back 401.
        handleUnauthorized();
        return;
      }
      await load();
    } catch (err) {
      setError(toMessage(err));
    }
  }

  return (
    <div className="card stack">
      <div className="row row--between">
        <h2>Parents</h2>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => setInviting(true)}
        >
          Invite a co-parent
        </button>
      </div>

      <ErrorMessage>{error}</ErrorMessage>

      <div className="stack">
        {parents.map((p) => (
          <div key={p.id} className="row row--between">
            <span>
              {p.displayName}
              {p.id === user?.id ? " (you)" : ""}{" "}
              <span className="faint">{p.email}</span>
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={parents.length <= 1}
              onClick={() => setRemoving(p)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      {inviting ? (
        <InviteParentModal onClose={() => setInviting(false)} />
      ) : null}

      {removing ? (
        <Modal title={`Remove ${removing.displayName}?`} onClose={() => setRemoving(null)}>
          <p className="muted">
            {removing.id === user?.id
              ? "You'll be signed out immediately."
              : `${removing.displayName} will no longer be able to sign in as a parent of this family.`}
          </p>
          <div className="row row--between">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setRemoving(null)}
            >
              Cancel
            </button>
            <button type="button" className="btn btn--danger" onClick={doRemove}>
              Remove
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/** Generates a single-use invite code and shows it once — there is no route
 * to read it back out after this. */
function InviteParentModal({ onClose }: { onClose: () => void }) {
  const toMessage = useErrorHandler();

  const [invite, setInvite] = useState<ParentInvite | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .createParentInvite()
      .then((inv) => {
        if (!cancelled) setInvite(inv);
      })
      .catch((err) => {
        if (!cancelled) setError(toMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [toMessage]);

  async function copy() {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.code);
      setCopied(true);
    } catch {
      // Clipboard access can be denied; the code is still shown on screen.
    }
  }

  return (
    <Modal title="Invite a co-parent" onClose={onClose}>
      <div className="stack">
        <ErrorMessage>{error}</ErrorMessage>

        {invite ? (
          <>
            <p className="muted">
              Share this code with them. It works once, and expires in 7
              days.
            </p>
            <div className="row row--between">
              <span className="family-code">{invite.code}</span>
              <button type="button" className="btn btn--ghost" onClick={copy}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="faint">
              They'll enter it, along with their own name, email, and
              password, on the "Join a family" screen.
            </p>
          </>
        ) : error ? null : (
          <p className="muted">Generating…</p>
        )}

        <div className="row row--between">
          <span />
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Balance, ledger, and the money controls for one child. */
function ChildDetail({
  child,
  onChildChanged,
}: {
  child: User;
  onChildChanged: () => Promise<void>;
}) {
  const toMessage = useErrorHandler();

  const [balance, setBalance] = useState<Balance | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [allowances, setAllowances] = useState<RecurringAllowance[]>([]);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Transaction | null>(null);
  const [confirmDeleteChild, setConfirmDeleteChild] = useState(false);
  const [allowanceModalOpen, setAllowanceModalOpen] = useState(false);
  const [editingAllowance, setEditingAllowance] =
    useState<RecurringAllowance | null>(null);
  const [confirmStopAllowance, setConfirmStopAllowance] =
    useState<RecurringAllowance | null>(null);

  const reload = useCallback(async () => {
    setError("");
    try {
      // Balance is re-fetched rather than adjusted locally: the server derives
      // it from the ledger, and that is the number that must be believed.
      //
      // The child record is re-read too, which is easy to forget: everything
      // else here is fetched, but the child arrives as a prop and so used to
      // stay frozen at whatever it was when the page loaded. A kid changing
      // their Spend/Save split in another window left this screen quoting the
      // old percentage — the money still landed correctly, since the server
      // reads the split when it writes, but the parent was shown a number that
      // was not true.
      const [bal, cats, page, rules] = await Promise.all([
        api.balance(child.id),
        api.listCategories(child.id),
        api.listTransactions(child.id, { limit: PAGE_SIZE }),
        api.listAllowances(child.id),
        onChildChanged(),
      ]);
      setBalance(bal);
      setCategories(cats);
      setTransactions(page.transactions);
      setNextCursor(page.nextCursor);
      setAllowances(rules.allowances);
    } catch (err) {
      setError(toMessage(err));
    }
  }, [child.id, onChildChanged, toMessage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Keep the screen current the way the child's does. A parent's view goes
  // stale for the same reasons: the allowance runner pays out, a kid spends
  // their own money, a kid changes their split. None of those are things the
  // parent did, so nothing on this page would otherwise re-fetch.
  usePoll(reload, POLL_MS);

  async function loadMore() {
    if (!nextCursor) return;
    try {
      const page = await api.listTransactions(child.id, {
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      setTransactions((prev) => [...prev, ...page.transactions]);
      setNextCursor(page.nextCursor);
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function doDelete(tx: Transaction) {
    try {
      await api.deleteTransaction(tx.id);
      setConfirmDelete(null);
      await reload();
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function doDeleteChild() {
    try {
      await api.deleteChild(child.id);
      setConfirmDeleteChild(false);
      await onChildChanged();
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function toggleAllowance(a: RecurringAllowance) {
    try {
      await api.updateAllowance(a.id, { active: !a.active });
      await reload();
    } catch (err) {
      setError(toMessage(err));
    }
  }

  async function stopAllowance(a: RecurringAllowance) {
    try {
      await api.deleteAllowance(a.id);
      setConfirmStopAllowance(null);
      await reload();
    } catch (err) {
      setError(toMessage(err));
    }
  }

  return (
    <div className="stack">
      <ErrorMessage>{error}</ErrorMessage>

      <h2>{child.displayName}'s money</h2>

      {balance ? <BalanceView balance={balance} label="Total" /> : null}

      <div className="card card--flush">
        <div className="section-head">
          <h2>Recurring payments</h2>
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => {
              setEditingAllowance(null);
              setAllowanceModalOpen(true);
            }}
          >
            Add recurring payment
          </button>
        </div>

        <AllowanceList
          allowances={allowances}
          savePercent={child.savePercent}
          onEdit={(a) => {
            setEditingAllowance(a);
            setAllowanceModalOpen(true);
          }}
          onToggle={toggleAllowance}
          onDelete={(a) => setConfirmStopAllowance(a)}
        />
      </div>

      <div className="card card--flush">
        <div className="section-head">
          <h2>History</h2>
          <div className="row">
            <button
              type="button"
              className="btn btn--primary btn--small"
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
                // The form quotes the child's split, so fetch it now rather
                // than trusting a figure that may be most of a poll old. The
                // modal reads it as a prop and corrects itself when this
                // lands.
                void reload();
              }}
            >
              Add transaction
            </button>
            <button
              type="button"
              className="btn btn--danger btn--small"
              onClick={() => setConfirmDeleteChild(true)}
            >
              Remove kid
            </button>
          </div>
        </div>

        <TransactionList
          transactions={transactions}
          categories={categories}
          canEdit
          onEdit={(tx) => {
            setEditing(tx);
            setModalOpen(true);
          }}
          onDelete={(tx) => setConfirmDelete(tx)}
          emptyMessage="Nothing here yet. Add the first deposit."
        />

        {nextCursor ? (
          <div className="row" style={{ padding: "var(--gap) var(--gap-lg)" }}>
            <button type="button" className="btn btn--ghost" onClick={loadMore}>
              Load more
            </button>
          </div>
        ) : null}
      </div>

      {modalOpen ? (
        <TransactionModal
          childId={child.id}
          childName={child.displayName}
          childSavePercent={child.savePercent}
          categories={categories}
          existing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={async () => {
            setModalOpen(false);
            await reload();
          }}
        />
      ) : null}

      {allowanceModalOpen ? (
        <AllowanceModal
          childId={child.id}
          childName={child.displayName}
          existing={editingAllowance}
          onClose={() => setAllowanceModalOpen(false)}
          onSaved={async () => {
            setAllowanceModalOpen(false);
            await reload();
          }}
        />
      ) : null}

      {confirmStopAllowance ? (
        <Modal
          title="Stop this allowance?"
          onClose={() => setConfirmStopAllowance(null)}
        >
          <p className="muted">
            No more automatic payments. Money already paid stays in their
            history — this only stops future ones. To pause temporarily instead,
            use Pause.
          </p>
          <div className="row row--between">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirmStopAllowance(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => stopAllowance(confirmStopAllowance)}
            >
              Stop it
            </button>
          </div>
        </Modal>
      ) : null}

      {confirmDelete ? (
        <Modal title="Delete this transaction?" onClose={() => setConfirmDelete(null)}>
          <p className="muted">
            The balance will update automatically. This can't be undone.
          </p>
          <div className="row row--between">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirmDelete(null)}
            >
              Keep it
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => doDelete(confirmDelete)}
            >
              Delete
            </button>
          </div>
        </Modal>
      ) : null}

      {confirmDeleteChild ? (
        <Modal
          title={`Remove ${child.displayName}?`}
          onClose={() => setConfirmDeleteChild(false)}
        >
          <p className="muted">
            This deletes their profile, buckets, and their whole transaction
            history. This can't be undone.
          </p>
          <div className="row row--between">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirmDeleteChild(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={doDeleteChild}
            >
              Remove
            </button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function AddChildModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const toMessage = useErrorHandler();

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [avatar, setAvatar] = useState(DEFAULT_AVATAR);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.createChild({ displayName, username, pin, avatar });
      await onCreated();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Add a kid" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorMessage>{error}</ErrorMessage>

        <Field label="Name">
          <input
            className="input"
            autoFocus
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
          />
        </Field>

        <Field label="Username" hint="What they'll type to sign in">
          <input
            className="input"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Field>

        <Field label="PIN" hint="4 to 8 digits">
          <input
            className="input"
            inputMode="numeric"
            pattern="[0-9]*"
            minLength={4}
            maxLength={8}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            required
          />
        </Field>

        <Field label="Avatar" hint="They can change this themselves later">
          <AvatarPicker value={avatar} onPick={setAvatar} />
        </Field>

        <div className="row row--between">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
